import { IconChevronRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useMountTime } from "../../hooks/useMountTime";
import type { WizardQuestion } from "./WizardInvitation";

/**
 * The question card (Figma A1b, R1b, D-A1b, docs/juiscigpt-wizard.md REQ-11):
 * after "Plus tard", the tour question waits above the composer until the
 * first question (OD-08). "Adapter" puts it in the composer to edit, and
 * "Poser la question" sends it and starts the tour. Neither replaces a draft
 * (INV-04), nor sends before a file or a reference being added.
 */
export function WizardQuestionCard({
  question,
  isComposerFree,
  onAdapt,
  onAsk,
}: {
  question: WizardQuestion;
  // False while the composer holds a draft of the reader, or a file or a
  // reference being added.
  isComposerFree: boolean;
  onAdapt: (secondsShown: number) => void;
  onAsk: (secondsShown: number) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  // The seconds since the card showed, for the analytics of the choice (REQ-30).
  const shownAt = useMountTime();
  const secondsShown = (): number =>
    Math.round((performance.now() - shownAt) / 100) / 10;
  return (
    <section
      aria-label={t("Tour question")}
      className="flex w-full flex-col gap-2 rounded-3xl bg-ds-moss-950/90 p-4 text-ds-canvas shadow-xl"
    >
      <span className="text-xs opacity-66">
        {question.level === "specialty"
          ? t("A question chosen for your specialty")
          : t("A question of the moment")}
      </span>
      <p className="text-base font-semibold leading-snug">{question.text}</p>
      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          className="h-11 px-1 text-sm font-medium disabled:opacity-40"
          disabled={!isComposerFree}
          onClick={() => {
            onAdapt(secondsShown());
          }}
        >
          {t("Adapt")}
        </button>
        <button
          type="button"
          className="flex h-11 items-center gap-1 rounded-xl bg-ds-moss-300 pl-4 pr-3 text-sm font-medium text-ds-moss-950 ring-1 ring-ds-black/30 disabled:opacity-40"
          disabled={!isComposerFree}
          onClick={() => {
            onAsk(secondsShown());
          }}
        >
          {t("Ask the question")}
          <IconChevronRight className="size-4" stroke={1.8} />
        </button>
      </div>
    </section>
  );
}
